/**
 * Content Auto-Linker Utility
 * Automatically detects and links produce/recipe mentions in text content
 */

import linkingHelper from './linkingHelper';

// Mapping of ingredient variations to produce page slugs
const PRODUCE_MAPPING: { [key: string]: string } = {
  // Fruits
  'strawberry': 'strawberries',
  'strawberries': 'strawberries',
  'watermelon': 'watermelon',
  'watermelons': 'watermelon',
  'mango': 'mangoes',
  'mangoes': 'mangoes',
  'papaya': 'papayas',
  'papayas': 'papayas',
  'lime': 'key-limes',
  'limes': 'key-limes',
  'key lime': 'key-limes',
  'key limes': 'key-limes',

  // Vegetables
  'tomato': 'florida-tomatoes',
  'tomatoes': 'florida-tomatoes',
  'florida tomato': 'florida-tomatoes',
  'florida tomatoes': 'florida-tomatoes',
  'avocado': 'florida-avocados',
  'avocados': 'florida-avocados',
  'florida avocado': 'florida-avocados',
  'florida avocados': 'florida-avocados',
  'pepper': 'bell-peppers',
  'peppers': 'bell-peppers',
  'bell pepper': 'bell-peppers',
  'bell peppers': 'bell-peppers',
  'cucumber': 'cucumbers',
  'cucumbers': 'cucumbers',
  'zucchini': 'zucchini',
  'zucchinis': 'zucchini',
  'squash': 'yellow-squash',
  'yellow squash': 'yellow-squash',
  'butternut squash': 'butternut-squash',
  'butternut': 'butternut-squash',
  'acorn squash': 'acorn-squash',
  'acorn': 'acorn-squash',
  'pumpkin': 'pumpkins',
  'pumpkins': 'pumpkins',
  'eggplant': 'eggplant',
  'okra': 'okra',
  'corn': 'sweet-corn',
  'sweet corn': 'sweet-corn',
  'bean': 'green-beans',
  'beans': 'green-beans',
  'green bean': 'green-beans',
  'green beans': 'green-beans',
  'beet': 'beets',
  'beets': 'beets',
  'carrot': 'carrots',
  'carrots': 'carrots',
  'radish': 'radishes',
  'radishes': 'radishes',
  'sweet potato': 'sweet-potatoes',
  'sweet potatoes': 'sweet-potatoes',
  'yam': 'sweet-potatoes',
  'yams': 'sweet-potatoes',

  // Greens
  'kale': 'kale',
  'spinach': 'spinach',
  'lettuce': 'lettuce-mix',
  'lettuce mix': 'lettuce-mix',
  'salad mix': 'lettuce-mix',
  'arugula': 'arugula',
  'rocket': 'arugula',
  'collard': 'collard-greens',
  'collards': 'collard-greens',
  'collard greens': 'collard-greens',
  'chard': 'swiss-chard',
  'swiss chard': 'swiss-chard',

  // Herbs
  'basil': 'basil',
  'cilantro': 'cilantro',
  'coriander': 'cilantro',
};

// Common recipe keywords to link
const RECIPE_KEYWORDS = [
  'recipe',
  'recipes',
  'dish',
  'dishes',
  'meal',
  'meals',
];

interface LinkMatch {
  text: string;
  url: string;
  start: number;
  end: number;
  type: 'produce' | 'recipe' | 'farm';
}

class ContentAutoLinker {
  /**
   * Auto-link produce mentions in HTML content
   */
  autoLinkProduce(htmlContent: string): string {
    let linkedContent = htmlContent;
    const matches: LinkMatch[] = [];

    // Find all produce mentions
    Object.entries(PRODUCE_MAPPING).forEach(([keyword, slug]) => {
      // Create regex to match whole words (case-insensitive)
      const regex = new RegExp(`\\b(${keyword})\\b`, 'gi');
      let match;

      while ((match = regex.exec(htmlContent)) !== null) {
        // Skip if inside an existing link
        if (this.isInsideLink(htmlContent, match.index)) {
          continue;
        }

        matches.push({
          text: match[1],
          url: linkingHelper.getProductUrl(slug),
          start: match.index,
          end: match.index + match[1].length,
          type: 'produce',
        });
      }
    });

    // Sort matches by start position (descending) to replace from end to start
    matches.sort((a, b) => b.start - a.start);

    // Replace matches with links
    matches.forEach((match) => {
      const before = linkedContent.substring(0, match.start);
      const after = linkedContent.substring(match.end);
      const link = `<a href="${match.url}" class="auto-linked-produce" style="color: #2D5A27; font-weight: 600; text-decoration: underline;">${match.text}</a>`;
      linkedContent = before + link + after;
    });

    return linkedContent;
  }

  /**
   * Check if position is inside an existing link or HTML tag
   */
  private isInsideLink(content: string, position: number): boolean {
    // Check for <a> tag before position
    const beforeContent = content.substring(0, position);
    const lastOpenTag = beforeContent.lastIndexOf('<a');
    const lastCloseTag = beforeContent.lastIndexOf('</a>');

    // If last <a> comes after last </a>, we're inside a link
    if (lastOpenTag > lastCloseTag) {
      return true;
    }

    // Check if inside any HTML tag
    const lastOpenBracket = beforeContent.lastIndexOf('<');
    const lastCloseBracket = beforeContent.lastIndexOf('>');
    if (lastOpenBracket > lastCloseBracket) {
      return true;
    }

    return false;
  }

  /**
   * Extract produce mentions from text
   */
  extractProduceMentions(text: string): string[] {
    const mentioned: Set<string> = new Set();

    Object.entries(PRODUCE_MAPPING).forEach(([keyword, slug]) => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      if (regex.test(text)) {
        mentioned.add(slug);
      }
    });

    return Array.from(mentioned);
  }

  /**
   * Get produce slug from ingredient name
   */
  getProduceSlug(ingredientName: string): string | null {
    const lowerName = ingredientName.toLowerCase().trim();
    return PRODUCE_MAPPING[lowerName] || null;
  }

  /**
   * Check if text mentions a specific produce item
   */
  mentionsProduce(text: string, produceSlug: string): boolean {
    const lowerText = text.toLowerCase();

    // Find all keywords that map to this slug
    const keywords = Object.entries(PRODUCE_MAPPING)
      .filter(([_, slug]) => slug === produceSlug)
      .map(([keyword]) => keyword);

    return keywords.some((keyword) => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      return regex.test(lowerText);
    });
  }

  /**
   * Create ingredient link markup
   */
  createIngredientLink(ingredientName: string, className: string = ''): string {
    const slug = this.getProduceSlug(ingredientName);
    if (!slug) {
      return ingredientName;
    }

    const url = linkingHelper.getProductUrl(slug);
    const classes = className ? ` class="${className}"` : '';
    return `<a href="${url}"${classes} style="color: #2D5A27; font-weight: 600; text-decoration: underline;">${ingredientName}</a>`;
  }

  /**
   * Wrap ingredient names in links for display
   */
  linkIngredientList(ingredients: string[]): string {
    return ingredients
      .map((ing) => this.createIngredientLink(ing, 'ingredient-link'))
      .join(', ');
  }

  /**
   * Get all available produce items
   */
  getAllProduceItems(): string[] {
    return Array.from(new Set(Object.values(PRODUCE_MAPPING)));
  }

  /**
   * Add related content suggestions based on text analysis
   */
  suggestRelatedContent(text: string): {
    produce: string[];
    recipes: string[];
  } {
    const produce = this.extractProduceMentions(text);

    // Keywords that suggest recipe content
    const hasRecipeKeywords = RECIPE_KEYWORDS.some((keyword) =>
      text.toLowerCase().includes(keyword)
    );

    return {
      produce,
      recipes: hasRecipeKeywords ? ['suggested-recipes'] : [],
    };
  }

  /**
   * Create contextual link suggestions for content
   */
  getContextualLinks(contentType: string, mainTopic: string): Array<{
    text: string;
    url: string;
    description: string;
  }> {
    const links: Array<{ text: string; url: string; description: string }> = [];

    // Add produce-specific links
    const produceSlug = this.getProduceSlug(mainTopic);
    if (produceSlug) {
      links.push({
        text: `All About ${mainTopic}`,
        url: linkingHelper.getProductUrl(produceSlug),
        description: `Learn more about growing and selecting ${mainTopic}`,
      });
      links.push({
        text: `${mainTopic} Recipes`,
        url: linkingHelper.getSearchUrl(mainTopic, { type: 'recipe' }),
        description: `Find recipes featuring ${mainTopic}`,
      });
    }

    // Add general useful links based on content type
    if (contentType === 'blog') {
      links.push({
        text: 'Seasonal Calendar',
        url: '/seasonal',
        description: "See what's in season now",
      });
      links.push({
        text: 'Find Local Farms',
        url: '/browse',
        description: 'Browse farms and markets near you',
      });
    }

    return links;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new ContentAutoLinker();
